import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Pencil, Plus, X } from 'lucide-react';
import { Toggle } from '@vibe/core';
import FieldTypeIcon from '../../editor/classic-form/center-editor/FieldTypeIcon';
import {
  KIND,
  MAX_SIGNERS,
  PREFILL_STYLE,
  PREFILL_DEFAULT_SIZE,
  SIGNER_FIELD_TYPES,
  SIGNER_FIELD_TYPE_LIST,
  signerColor,
} from './sign-overlays';
import { signerChecklist } from './validation';

/** MIME type for palette→canvas drags; SmartSign's own so drops can't cross apps. */
export const DRAG_MIME = 'application/x-smartsign-field';

/**
 * SignFieldsPalette — left pane of step 2 (§4).
 *
 * Two groups, two kinds of fields:
 *   1. Signer fields · filled at signing — headed by the signer roster, one row
 *      per signer, where renaming and removing also happen. The selected row is
 *      the ACTIVE signer: every chip dropped belongs to it and wears its
 *      colour.
 *   2. Item data · prefilled — board columns, blue, filled at generation.
 *
 * Signers first: step 2's job is "who signs what", and the parties are the
 * thing you pick before you place anything. Item data is the supporting cast —
 * it needs no decision, it fills itself in.
 *
 * Footer: the item-field count. Per-signer signature coverage sits on the
 * signer rows themselves.
 */
export default function SignFieldsPalette({
  template,
  prefillColumns,
  activeSigner,
  onSelectSigner,
  onAddSigner,
  onRemoveSigner,
  onRenameSigner,
  placedColumnIds,
  armedChip,
  onArmChip,
  /** Rendered page size in CSS px, from the canvas. Null until the PDF loads. */
  pagePx,
  /** 'sequential' | 'parallel'. Omit on surfaces that have no send. */
  signingOrder,
  onChangeSigningOrder,
}) {
  // Envelopes carry `signers` (real people), templates carry `roles` (unfilled
  // slots). The palette only needs labels and signature coverage, so it takes
  // whichever is present rather than caring which surface it is on.
  const signers = template.signers || template.roles || [];
  const checklist = signerChecklist(template);

  // Order only means something with someone to be ahead of: at one signer the
  // switch and the numbering are both hidden rather than shown answering a
  // question nobody asked.
  const orderMatters = Boolean(onChangeSigningOrder) && signers.length > 1;
  const sequential = (signingOrder || 'sequential') === 'sequential';
  const numbered = orderMatters && sequential;

  /**
   * Inline rename, held as a draft rather than written through on every
   * keystroke.
   *
   * The label is load-bearing, not cosmetic: roles merge BY LABEL when
   * templates are copied into an envelope, so "Client" and "Candidate" stay two
   * people where a half-typed "C" twice would briefly read as one. Committing
   * on Enter or blur means the merge only ever sees names someone finished
   * typing.
   */
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const editNewRef = useRef(false);

  const startEdit = (i) => {
    setDraft(signers[i]?.roleLabel || '');
    setEditing(i);
  };

  const commitEdit = () => {
    if (editing === null) return;
    const next = draft.trim();
    // Empty reverts. A blank label is not a name anyone chose, and merging by
    // label would make two blanks one person.
    if (next && next !== signers[editing]?.roleLabel) onRenameSigner(editing, next);
    setEditing(null);
  };

  const addSigner = () => {
    // Open the new row for naming straight away. The old always-visible "Role
    // name" input made naming the obvious next step; without that, adding a
    // signer would otherwise leave you with a "Signer 2" nobody chose.
    // Guarded on the callback, or a caller that can add but not rename would
    // open an editor with nothing to commit to.
    editNewRef.current = Boolean(onRenameSigner);
    onAddSigner();
  };

  // The roster changed under an open editor. Indices shift on removal, so the
  // editor would be pointed at whoever moved into that slot — close it, unless
  // this is the signer we just added, which opens for naming instead.
  useEffect(() => {
    if (editNewRef.current && signers.length) {
      editNewRef.current = false;
      setDraft(signers[signers.length - 1]?.roleLabel || '');
      setEditing(signers.length - 1);
    } else {
      setEditing(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signers.length]);

  // Envelope-wide, not per-document, matching the checklist's signature test: a
  // signature on document 2 counts for that signer whatever page you are on.
  const fieldCounts = useMemo(() => {
    const counts = new Array(signers.length).fill(0);
    for (const f of template.signerFields || []) {
      if (counts[f.signerIndex] !== undefined) counts[f.signerIndex] += 1;
    }
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.signerFields, signers.length]);

  /**
   * The drag ghost, sized to the box the drop will actually create.
   *
   * With no drag image set, the browser drags a picture of the element the
   * gesture started on — a 258px-wide sidebar button — so the thing under the
   * cursor bore no relation to the box that appeared on release. Same normalised
   * default size the canvas uses, times the rendered page size, so the two
   * cannot disagree.
   *
   * A real node in the document is required: `setDragImage` ignores anything not
   * laid out, and `visibility:hidden`/`display:none` produce a blank ghost. So it
   * is parked off-screen and removed on dragend.
   */
  const ghostRef = useRef(null);

  const buildGhost = (payload) => {
    if (!pagePx?.width) return null;
    const isSigner = payload.kind === KIND.SIGNER;
    const size = isSigner
      ? (SIGNER_FIELD_TYPES[payload.type] || SIGNER_FIELD_TYPES.text).defaultSize
      : PREFILL_DEFAULT_SIZE;
    const style = isSigner ? signerColor(activeSigner) : PREFILL_STYLE;

    const node = document.createElement('div');
    node.textContent = isSigner
      ? `${(SIGNER_FIELD_TYPES[payload.type] || SIGNER_FIELD_TYPES.text).verb} — ${
          signers[activeSigner]?.roleLabel || `Signer ${activeSigner + 1}`
        }`
      : `{${prefillColumns.find((c) => c.id === payload.columnId)?.title || payload.columnId}}`;
    Object.assign(node.style, {
      position: 'fixed',
      top: '-1000px',
      left: '-1000px',
      width: `${Math.round(size.w * pagePx.width)}px`,
      height: `${Math.round(
        isSigner && SIGNER_FIELD_TYPES[payload.type]?.square
          ? size.w * pagePx.width
          : size.h * pagePx.height,
      )}px`,
      border: style.border,
      background: style.background,
      color: style.color,
      borderRadius: isSigner && payload.type === 'signature' ? '4px' : '0',
      font: '500 8.5px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    });
    return node;
  };

  const startDrag = (e, payload) => {
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'copy';

    const ghost = buildGhost(payload);
    if (!ghost) return;
    document.body.appendChild(ghost);
    ghostRef.current = ghost;
    // Centred on the cursor, matching `addField`, which centres the new box on
    // the drop point — so the ghost sits exactly where the box will land.
    e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2);
  };

  const endDrag = () => {
    ghostRef.current?.remove();
    ghostRef.current = null;
  };

  // A drag abandoned by unmounting (switching document, leaving step 2) fires no
  // dragend, so the parked node would outlive the palette.
  useEffect(() => endDrag, []);

  const chipIsArmed = (payload) =>
    armedChip &&
    armedChip.kind === payload.kind &&
    (payload.kind === KIND.PREFILL
      ? armedChip.columnId === payload.columnId
      : armedChip.type === payload.type);

  return (
    <aside className='flex flex-col flex-shrink-0 w-64 bg-white border-r border-gray-200 overflow-hidden'>
      <div className='flex-1 overflow-y-auto px-3 py-4'>
        {/* ── Group 1: signer fields ──
          The roster heads the group and the draggable chips follow it, because
          they are one idea: pick who, then place what they sign. Only the
          roster gets a heading — the chips need no second one naming the active
          signer, since they are tinted in that signer's colour, the selected
          row is directly above wearing the same colour, and the box lands on
          the page in it too.

          The roster itself is one row per signer, and the row you pick owns the
          next field you drop.

          Was a horizontal chip row, which laid the roster out along the axis
          this rail has no room in: 232px of inner width, minus the gaps and the
          add button, is ~37px a chip at MAX_SIGNERS, and the colour dot takes 12
          of that. Labels truncated to "Prop…" by the third signer, and there was
          nowhere to hang a rename control — which is why renaming needed its own
          "Role name" input further down, one step removed from the signer it
          renamed and always pointed at whichever signer happened to be active.

          A row gets the full width instead: the label never truncates at any
          count up to the cap, the status sits beside it, and the pencil and ×
          are attached to the signer they act on.
        ── */}
        <p className='px-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400'>
          Signer fields · filled at signing
        </p>
        {/*
          One switch, not two tabs. Sequential is the default and the common
          case, so a 50/50 segmented control overstated the choice.

          ABOVE the roster, not below it: it governs the list, and a control
          that reads as a heading for what follows needs no explaining that it
          applies to them. Turning it on numbers the rows and threads them
          together, so the effect is visible in the same glance as the cause.

          The subtitle describes what the switch TURNS ON and does not change
          with its state: a label that rewrites itself under your finger makes
          you read it twice to find out which way you just went.
        */}
        {orderMatters && (
          <div className='flex items-start gap-2 px-2 pb-2.5 mb-1.5 border-b border-gray-100'>
            {/*
              monday's own Toggle rather than a hand-rolled one. The version
              here before it positioned its knob absolutely inside a 28px
              pill and rendered without it — and a switch is exactly the kind
              of control not worth owning: the platform ships one that already
              matches every other toggle the user sees, including its focus
              ring, its motion and its dark mode.

              `areLabelsHidden` because the label lives beside it, and
              `noSpacing` because the flex gap owns the spacing.
            */}
            <div className='flex-shrink-0 mt-0.5'>
              <Toggle
                size='small'
                areLabelsHidden
                noSpacing
                ariaLabel='Sign in order'
                isSelected={sequential}
                onChange={(value) => onChangeSigningOrder(value ? 'sequential' : 'parallel')}
              />
            </div>
            <div className='min-w-0'>
              <div className='text-[11px] font-medium text-gray-700'>Sign in order</div>
              <p className='text-[10px] leading-snug text-gray-400'>
                Each signer is invited when the one before them finishes.
              </p>
            </div>
          </div>
        )}


        <div className='px-1 pb-4'>
          {signers.map((signer, i) => {
            const c = signerColor(i);
            const active = activeSigner === i;
            const label = signer.roleLabel || `Signer ${i + 1}`;

            /*
              The thread. Drawn in the gap ABOVE each row but the first, so the
              waiting between two people is a mark on the page rather than
              something inferred from two numerals. Only when the order is on —
              in parallel there is nothing joining them.

              Costs 12px a gap, which is why the numbering alone was the other
              candidate: at five signers this is 48px of a rail that also holds
              the field chips.
            */
            const thread = numbered && i > 0 && (
              <span aria-hidden className='flex items-center h-3 pl-[14px]'>
                <span className='w-px h-3' style={{ background: '#E2E8F0' }} />
              </span>
            );

            if (editing === i) {
              return (
                <React.Fragment key={i}>
                {thread}
                <div
                  className='flex items-center w-full gap-2 px-2 py-1 mb-0.5 bg-white rounded-md'
                  style={{ border: `1px solid ${c.color}` }}
                >
                  <span className='w-2 h-2 rounded-full flex-shrink-0' style={{ background: c.color }} />
                  <input
                    autoFocus
                    type='text'
                    value={draft}
                    maxLength={80}
                    placeholder={`Signer ${i + 1}`}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitEdit();
                      }
                      if (e.key === 'Escape') {
                        // Stopped, or the wizard modal takes the Escape and the
                        // whole editor closes over an abandoned rename.
                        e.preventDefault();
                        e.stopPropagation();
                        setEditing(null);
                      }
                    }}
                    className='flex-1 min-w-0 py-0.5 text-sm text-gray-900 bg-transparent border-0 outline-none'
                  />
                </div>
                </React.Fragment>
              );
            }

            const count = fieldCounts[i] || 0;

            return (
              <React.Fragment key={i}>
              {thread}
              <button
                type='button'
                onClick={() => onSelectSigner(i)}
                className='group flex items-center w-full gap-2 px-2 py-1.5 mb-0.5 text-left rounded-md text-xs font-medium transition-colors'
                style={{
                  background: active ? c.background : 'transparent',
                  border: `1px solid ${active ? c.color : 'transparent'}`,
                  color: active ? c.color : 'var(--color-text-secondary, #475569)',
                }}
              >
                {/*
                  The dot GROWS into the badge rather than being replaced by it:
                  the colour is load-bearing — it is how this row ties to the
                  boxes on the page — so the position rides on the colour
                  instead of taking its place.
                */}
                {numbered ? (
                  <span
                    className='flex items-center justify-center flex-shrink-0 rounded-full'
                    style={{ width: 15, height: 15, background: c.color, color: '#fff', fontSize: 9, fontWeight: 700 }}
                  >
                    {i + 1}
                  </span>
                ) : (
                  <span className='w-2 h-2 rounded-full flex-shrink-0' style={{ background: c.color }} />
                )}
                {/*
                  Plain text, with no click of its own.

                  Clicking the label of the already-active row used to start an
                  inline rename — a "quick path" that made sense when selecting a
                  signer did nothing else. It does something else now: it opens
                  the properties panel, which is the only place an email address
                  can be set. Two behaviours on one target meant the second click
                  on a row put you in a rename box instead of the panel you were
                  reaching for, and there was no way back to it.

                  The pencil is the rename affordance, and now the only one.
                */}
                <span className='flex-1 min-w-0 truncate'>{label}</span>

                {/*
                  Status, which steps aside on hover to make room for the
                  controls. The active row shows both — it has the width, and it
                  means the controls are never hover-only, which is the whole
                  reason this works on touch: tapping a row makes it the active
                  one.

                  Just the count. A "no signature" flag and then a "no address"
                  one both sat here, and both said what the footer checklist now
                  says by name and grouped under the person. A marker whose only
                  job is to repeat the thing beside the Send button is noise on
                  the row and does not make the problem any clearer.
                */}
                <span className={`flex items-center flex-shrink-0 gap-1 ${active ? '' : 'group-hover:hidden'}`}>
                  <span
                    className='text-[10px] font-normal text-gray-400'
                    title={`${count} field${count === 1 ? '' : 's'} placed`}
                  >
                    {active ? `${count} field${count === 1 ? '' : 's'}` : count}
                  </span>
                </span>

                {/* spans, not buttons — buttons cannot nest. The remove confirm
                    and the field clean-up both live in the parent handler. */}
                <span className={`items-center flex-shrink-0 ${active ? 'flex' : 'hidden group-hover:flex'}`}>
                  {onRenameSigner && (
                    <span
                      role='button'
                      aria-label={`Rename ${label}`}
                      title='Rename this signer'
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(i);
                      }}
                      className='p-0.5 rounded hover:bg-black/5'
                      style={{ lineHeight: 0 }}
                    >
                      <Pencil size={11} />
                    </span>
                  )}
                  {signers.length > 1 && (
                    <span
                      role='button'
                      aria-label={`Remove ${label}`}
                      title='Remove this signer'
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveSigner(i);
                      }}
                      className='p-0.5 rounded hover:bg-black/5'
                      style={{ lineHeight: 0 }}
                    >
                      <X size={11} />
                    </span>
                  )}
                </span>
              </button>
              </React.Fragment>
            );
          })}

          {signers.length < MAX_SIGNERS ? (
            <button
              type='button'
              onClick={addSigner}
              className='flex items-center justify-center w-full gap-1.5 px-2 py-1.5 mt-1 text-xs font-medium text-gray-400 transition-colors border border-dashed border-gray-300 rounded-md hover:text-gray-600 hover:border-gray-400'
            >
              <Plus size={12} /> Add signer
            </button>
          ) : (
            /* A statement rather than a disabled button: there is no action left
               to offer, and a greyed-out + invites clicking to find out why. */
            <p className='px-2 py-1.5 mt-1 text-[11px] text-center text-gray-300 border border-gray-100 rounded-md'>
              All {MAX_SIGNERS} signers added
            </p>
          )}

        </div>

        {SIGNER_FIELD_TYPE_LIST.map((meta) => {
          const payload = { kind: KIND.SIGNER, type: meta.type, signerIndex: activeSigner };
          const armed = chipIsArmed(payload);
          const c = signerColor(activeSigner);
          const Icon = meta.icon;
          return (
            <button
              key={meta.type}
              type='button'
              draggable
              onDragStart={(e) => startDrag(e, payload)}
              onDragEnd={endDrag}
              onClick={() => onArmChip(armed ? null : payload)}
              title={armed ? 'Now click a spot on the page to place it' : 'Drag onto the page, or click then click the page'}
              className='flex items-center w-full gap-2 px-2 py-1.5 mb-1 text-left rounded-md transition-colors'
              style={{
                background: armed ? c.background : c.chipBackground,
                border: armed ? `1px solid ${c.color}` : c.chipBorder,
                cursor: 'grab',
              }}
            >
              <Icon size={13} style={{ color: c.color, flexShrink: 0 }} />
              <span className='flex-1 min-w-0 text-sm truncate text-gray-900'>{meta.label}</span>
            </button>
          );
        })}
        {/* ── Group 2: item data ── */}
        <p className='px-2 pt-5 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400'>
          Item data · prefilled
        </p>
        <p className='px-2 pb-2 text-xs leading-snug text-gray-500'>
          Filled automatically from the item when the document is generated
        </p>
        {prefillColumns.map((col) => {
          const payload = { kind: KIND.PREFILL, columnId: col.id };
          const armed = chipIsArmed(payload);
          const placed = placedColumnIds.has(col.id);
          return (
            <button
              key={col.id}
              type='button'
              draggable
              onDragStart={(e) => startDrag(e, payload)}
              onDragEnd={endDrag}
              onClick={() => onArmChip(armed ? null : payload)}
              title={armed ? 'Now click a spot on the page to place it' : 'Drag onto the page, or click then click the page'}
              className='flex items-center w-full gap-2 px-2 py-1.5 mb-1 text-left rounded-md transition-colors'
              style={{
                background: armed ? 'rgba(37,99,235,0.16)' : PREFILL_STYLE.chipBackground,
                border: armed ? `1px solid ${PREFILL_STYLE.color}` : PREFILL_STYLE.chipBorder,
                cursor: 'grab',
              }}
            >
              <FieldTypeIcon type={col.type} size='bare' />
              <span className='flex-1 min-w-0 text-sm truncate text-gray-900'>{col.title}</span>
              {/* A column may sit on several pages — check = "placed at least once", chip stays draggable. */}
              {placed && <Check size={14} className='flex-shrink-0' style={{ color: '#00854d' }} />}
            </button>
          );
        })}

      </div>

      {/*
        Footer (§4). The per-signer checklist that used to live here is now on
        the signer rows: it was reporting, from the bottom of the rail, on
        signers named at the top of it, and the wizard footer says the same
        thing again on the way out of the step. Saying it beside the signer is
        what pays for the rows being taller than the chips they replaced.

        The item-field count stays — it belongs to no single row, and there is
        nowhere else for it to go.
      */}
      <div className='flex-shrink-0 px-4 py-3 text-xs text-gray-500 border-t border-gray-100'>
        {checklist.prefillCount} item {checklist.prefillCount === 1 ? 'field' : 'fields'} placed
      </div>
    </aside>
  );
}
